/**
 * GAP4-08 (#337), GAP4-10 (#340), TAG-18 (#355): the lifecycle events.
 *
 * The 2026-09-20 audit found a Google Tag Manager container fed nothing: not one push for a
 * sign-up, a lead or a download, which is part of why nobody noticed 294 pages showing a
 * newsletter heading over an empty form. This module supplies the numbers. It pushes these
 * events, each with exactly the keys listed and nothing else:
 *
 *   newsletter_signup_submitted  form_name, form_location     submit of a newsletter form
 *   newsletter_signup_completed  form_name, form_location     the next load, if that form succeeded
 *   lead_form_submitted          form_name, form_location     submit of a contact or B2B form
 *   lead_form_completed          form_name, form_location     the next load, if that form succeeded
 *   content_downloaded           content_name, content_type   click on a [data-wf-download] link
 *   pet_breed_selected           breed_name                   wf:breed-selected from the breed modal
 *   page_not_found               no keys                      load of the 404 page
 *   size_guide_opened            no keys                      click on a [data-wf-size-guide] link
 *   bmi_calculated               no keys                      the BMI calculator shows a result
 *
 * The markup carries the names. Every lead <form> has data-wf-lead-form and
 * data-wf-lead-location; inside it, the element that shows success has
 * data-wf-lead-result="success" and the one that shows form.errors has
 * data-wf-lead-result="error"; a download link has data-wf-download; the 404 section
 * (sections/s-404.liquid) has data-wf-page-not-found, on the 404 template only. No payload ever
 * carries a field value: the names come from attributes the theme writes, and a value is sent only
 * when it is a known form name or a short snake_case token, which no address or URL can be.
 * page_not_found carries nothing but its name: GA4 records the page location with every event
 * itself, and a path or referrer pushed here could hold whatever a mistyped link held.
 *
 * THE LOCATIONS. form_location is the form's data-wf-lead-location token, reported as written: any
 * snake_case token of up to 40 characters, so a new place needs no code here, only a line below.
 * The theme writes home_band (sections/woopf-home-newsletter.liquid on the home page and the
 * woopf-home page template), collection_band (the same band on a collection template; collections
 * spec 5.3), contact_band (the same band on the woopf-contact page template; contact spec
 * 2026-10-14 5.5), about_band (the same band on the woopf-about page template; about spec
 * 2026-10-14 5.11), popup (sections/woopf-newsletter-popup.liquid), subscription_band
 * (sections/s-subscription.liquid), article_band (snippets/section-subscribe.liquid), contact_page
 * (sections/s-contact-us.liquid and sections/woopf-contact-form.liquid), trade_page
 * (sections/woopf-doc-form.liquid) and b2b_page (sections/main-b2b-login.liquid).
 * article_band also comes from sections/woopf-home-newsletter.liquid on an article template
 * (ADR 0009), the redesigned article page's band. blog_band comes from the same band on a blog
 * template (blog spec 2026-10-13 5.4), in place of the subscription_band and article_band today's
 * blog page sends.
 *
 * WHY dataLayer. As in assets/woopf-outbound.js: snippets/woopf-consent-gate.liquid creates
 * window.dataLayer synchronously and boots GTM only once the Customer Privacy API allows
 * analytics, so a push is consent-respecting by construction. Nothing here opens a connection.
 *
 * WHY A PENDING KEY. Shopify's forms post and reload the page, and its posted state is page-wide:
 * after one customer form is posted, every customer form on the page renders the success or the
 * errors. The result markup alone cannot say which form was sent. So on submit this module
 * writes sessionStorage['wf-lead-pending'] = {"id", "form_name", "form_location"}; on the next
 * load it reads and deletes the key, pushes the completed event only if the form with that id
 * holds a success marker, and hides the result markers of every other lead form. No key (the
 * submit happened without JavaScript, or storage is blocked) means no completed event and every
 * result left as Shopify rendered it. Storage failing is never an error.
 *
 * KNOWN LIMITS, all of them undercounts.
 * - A *_submitted event counts an attempt. The contact form's own validation
 *   (assets/section-contact-us.js) stops every submit at the form and refuses some, after this
 *   capturing listener has counted them. A refused attempt still writes the pending key; the next
 *   load finds no success marker for it and deletes it without an event. *_completed is the
 *   number to report and to mark as a conversion. Each form counts once per page view, so a
 *   double click is one attempt.
 * - A post Shopify routes through its /challenge page is reported to come back without the
 *   posted state, and the challenge page's own load consumes the key: no completed event.
 * - An address that is already subscribed is reported to come back with neither success nor
 *   errors: no completed event, which is right for a sign-up that created nothing.
 *
 * ADDING AN EVENT. Export a pure describe*() that returns the payload or null, an on*() handler
 * that sends it through pushEvent(), and one listener in start().
 *
 * Tests: tests/assets/lifecycle-events.test.mjs, tests/sections/lifecycle-markup.test.mjs.
 */

/** The sessionStorage key that carries a lead form's post to the next page load. */
export const PENDING_KEY = 'wf-lead-pending';

/** The lead forms this module knows, by data-wf-lead-form, and the two events each pushes. */
export const LEAD_EVENTS = Object.freeze({
  newsletter: Object.freeze({
    submitted: 'newsletter_signup_submitted',
    completed: 'newsletter_signup_completed',
  }),
  contact: Object.freeze({
    submitted: 'lead_form_submitted',
    completed: 'lead_form_completed',
  }),
  b2b_application: Object.freeze({
    submitted: 'lead_form_submitted',
    completed: 'lead_form_completed',
  }),
});

const LEAD_FORM = 'form[data-wf-lead-form]';
const RESULT = '[data-wf-lead-result]';
const SUCCESS = '[data-wf-lead-result="success"]';
const DOWNLOAD = '[data-wf-download]';
const PAGE_NOT_FOUND = '[data-wf-page-not-found]';
/** TAG-05 (#368): the product page's size guide link (snippets/block-size-comparison.liquid). */
export const SIZE_GUIDE = '[data-wf-size-guide]';
/** TAG-05 (#368): the BMI calculator's result heading (sections/s-bmi-calculator.liquid). */
export const BMI_RESULT = '[data-wf-bmi-result]';

/** A name the theme wrote: lower-case snake_case, at most 40 characters. */
const TOKEN = /^[a-z][a-z0-9_]{0,39}$/;
const token = (value) => (typeof value === 'string' && TOKEN.test(value) ? value : null);

/** An own key of LEAD_EVENTS, never an inherited one such as "constructor". */
const known = (name) =>
  typeof name === 'string' && Object.prototype.hasOwnProperty.call(LEAD_EVENTS, name);

/** Lead forms already counted on this page view. Weak, so a removed form is not kept alive. */
const counted = new WeakSet();

/**
 * The payload for one lead form at one phase ('submitted' or 'completed'), or null when the form
 * is not one this module knows. Pure, so the matching is tested without a DOM.
 */
export function describeLead(form, phase) {
  if (phase !== 'submitted' && phase !== 'completed') return null;
  const name = form?.getAttribute?.('data-wf-lead-form');
  const location = token(form?.getAttribute?.('data-wf-lead-location'));
  if (!known(name) || !location) return null;
  return { event: LEAD_EVENTS[name][phase], form_name: name, form_location: location };
}

/** The payload for a click on a download link, or null. Every download here is a PDF. */
export function describeDownload(link) {
  const name = token(link?.getAttribute?.('data-wf-download'));
  if (!name) return null;
  return { event: 'content_downloaded', content_name: name, content_type: 'pdf' };
}

/** The one way a payload leaves this module. */
export function pushEvent(scope, payload) {
  scope.dataLayer = scope.dataLayer || [];
  scope.dataLayer.push(payload);
}

/** window.sessionStorage, or null where even reading the property throws (storage blocked). */
function sessionStore(scope) {
  try {
    return scope.sessionStorage || null;
  } catch {
    return null;
  }
}

function rememberPending(scope, record) {
  try {
    sessionStore(scope)?.setItem(PENDING_KEY, JSON.stringify(record));
  } catch {
    // Full or blocked storage costs this post its completed event, and nothing else.
  }
}

/**
 * Read and delete the pending key: {id, form_name, form_location}, or null. A key that cannot be
 * deleted is ignored, because it would report the same post again on every reload.
 */
function takePending(scope) {
  const store = sessionStore(scope);
  if (!store) return null;
  let raw;
  try {
    raw = store.getItem(PENDING_KEY);
    if (raw === null) return null;
    store.removeItem(PENDING_KEY);
  } catch {
    return null;
  }
  let record;
  try {
    record = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!record || typeof record.id !== 'string' || record.id === '') return null;
  return { id: record.id, form_name: record.form_name, form_location: record.form_location };
}

/** Submit of a lead form: the submitted event, once per form per page view, and the key. */
export function onLeadSubmit(event, scope) {
  const form = event?.target?.closest?.(LEAD_FORM);
  if (!form) return;
  const payload = describeLead(form, 'submitted');
  if (!payload) return;
  if (!counted.has(form)) {
    counted.add(form);
    pushEvent(scope, payload);
  }
  if (form.id) {
    rememberPending(scope, {
      id: form.id,
      form_name: payload.form_name,
      form_location: payload.form_location,
    });
  }
}

/**
 * The load after a post: hide every other lead form's result, and push the completed event if
 * the posted form says success. Runs once, from start().
 */
export function settlePending(scope) {
  const doc = scope?.document;
  if (!doc) return;
  const pending = takePending(scope);
  if (!pending) return;

  let posted = null;
  for (const form of doc.querySelectorAll(LEAD_FORM)) {
    if (!posted && form.id === pending.id) {
      posted = form;
      continue;
    }
    for (const marker of form.querySelectorAll(RESULT)) {
      marker.hidden = true;
      marker.style.display = 'none';
    }
  }

  if (!posted || !posted.querySelector(SUCCESS)) return;
  const payload = describeLead(posted, 'completed');
  if (payload) pushEvent(scope, payload);
}

/** Click on a download link, or on anything inside one: one content_downloaded. */
export function onDownloadClick(event, scope) {
  const link = event?.target?.closest?.(DOWNLOAD);
  if (!link) return;
  const payload = describeDownload(link);
  if (payload) pushEvent(scope, payload);
}

/** TAG-05 (#368): a click inside the size guide link, or null. */
export function describeSizeGuide(target) {
  return target && typeof target.closest === 'function' && target.closest(SIZE_GUIDE)
    ? { event: 'size_guide_opened' }
    : null;
}

export function onSizeGuideClick(event, scope) {
  const payload = describeSizeGuide(event?.target);
  if (payload) pushEvent(scope, payload);
}

/** TAG-05 (#368): a result heading with text, or null. The text itself is never sent. */
export function describeBmiResult(element) {
  const text = typeof element?.textContent === 'string' ? element.textContent.trim() : '';
  return text ? { event: 'bmi_calculated' } : null;
}

/**
 * TAG-05 (#368): assets/s-bmi-calculator.js writes each result into the heading; every write is a
 * mutation batch, so a second calculation with the same result still counts, and a reset (empty
 * heading) does not.
 *
 * @returns {boolean} true when the heading is being watched
 */
export function watchBmiResult(scope) {
  const result = scope?.document?.querySelector?.(BMI_RESULT);
  if (!result || typeof scope.MutationObserver !== 'function') return false;
  const observer = new scope.MutationObserver(() => {
    const payload = describeBmiResult(result);
    if (payload) pushEvent(scope, payload);
  });
  observer.observe(result, { childList: true, characterData: true, subtree: true });
  return true;
}

/**
 * TAG-18 (#355): the payload for a load of the 404 page, or null on any other page. The marker
 * alone decides; the payload is the event name and nothing else.
 */
export function describePageNotFound(doc) {
  return doc?.querySelector?.(PAGE_NOT_FOUND) ? { event: 'page_not_found' } : null;
}

/** The load of the 404 page: one page_not_found. Runs once, from start(). */
export function onPageNotFound(scope) {
  const payload = describePageNotFound(scope?.document);
  if (payload) pushEvent(scope, payload);
}

/**
 * One delegated listener per event type, then what this load itself says: a 404 page, and the
 * post that brought the shopper here. Exported for the tests; the browser calls it once, below.
 */
export function start(scope) {
  const doc = scope.document;
  // Capture: assets/section-contact-us.js stops the contact form's submit at the form with
  // stopImmediatePropagation, so a bubbling listener here would never see that form.
  doc.addEventListener('submit', (event) => onLeadSubmit(event, scope), { capture: true });
  // Capture and passive, as in woopf-outbound.js: no handler below can hide the click, and
  // this can never be what delays the download.
  doc.addEventListener('click', (event) => onDownloadClick(event, scope), {
    capture: true,
    passive: true,
  });
  // More lifecycle signals: one listener each, added here, calling an exported handler above.
  /* GAP4-11 (#341): the breed modal's pick -> pet_breed_selected, and DogBreed in Klaviyo */
  listenForBreed(scope);
  /* TAG-18 (#355): a load of the 404 page -> page_not_found */
  onPageNotFound(scope);
  /* TAG-05 (#368): the size guide link -> size_guide_opened. On the window, capturing: the window
     hears a click before any document listener, so the size guide's own document-level capture
     handler (assets/section-main-product-size-compare.js), which stops the event, cannot pre-empt
     it whichever script registers first. */
  if (typeof scope.addEventListener === 'function') {
    scope.addEventListener('click', (event) => onSizeGuideClick(event, scope), {
      capture: true,
      passive: true,
    });
  }
  /* TAG-05 (#368): each BMI result -> bmi_calculated */
  watchBmiResult(scope);
  settlePending(scope);
}

/* ------------------------------------------------------------------------------------------- */

/**
 * GAP4-11 (#341): the breed signal.
 *
 * The breed modal used to keep the breed a visitor picks in that browser's localStorage and
 * nowhere else, although `sections/s-modal-gratitude.liquid` thanks them for sharing it and
 * promises "more personalized recommendations". `assets/breed-modal.core.js` now announces each
 * pick on the document as `wf:breed-selected` with `detail.breed` (the stored display name), at
 * the one moment it stores it; reading a stored breed back never announces. This block turns the
 * announcement into two things:
 *
 * 1. `pet_breed_selected` with `breed_name` on the dataLayer. No gate of its own: the consent gate
 *    (`snippets/woopf-consent-gate.liquid`) boots GTM only once analytics is allowed, so the push
 *    waits in the array like every other event in this module.
 * 2. `DogBreed` on the visitor's Klaviyo profile, only when Shopify's Customer Privacy API is
 *    loaded and `marketingAllowed()` returns true (round Decision 9). The API loads
 *    asynchronously (`assets/woopf-consent.js` requests it on every page, well before anyone can
 *    open the modal and pick); a pick made before it has loaded skips Klaviyo rather than
 *    waiting, so a breed is never held in memory for a consent answer that may not come.
 *
 * KLAVIYO. Klaviyo's Shopify app embed defines `window.klaviyo` inside content_for_header as a
 * Proxy that queues every call until klaviyo.js loads (read from woopf.com on 2026-09-24).
 * `klaviyo.identify(properties)` is Klaviyo's current onsite API and returns a Promise;
 * `klaviyo.push(['identify', properties])` is the documented queue form, used only when the page
 * holds the array the embed falls back to where Proxy is missing. The legacy `_learnq` is not
 * used, and this module never creates `window.klaviyo`: with the embed off there is nobody to
 * tell. Klaviyo documents that identify needs an email or phone number to identify someone; a
 * properties-only call is meant for a browser Klaviyo already knows (a Klaviyo form, an email or
 * SMS click, a checkout). What it does for an anonymous browser is not documented, and nothing
 * here relies on it.
 *
 * Sources: https://developers.klaviyo.com/en/docs/introduction_to_the_klaviyo_object,
 * https://developers.klaviyo.com/en/docs/javascript_api, https://shopify.dev/docs/api/customer-privacy
 */

/** The Klaviyo profile property the breed is saved under. */
export const BREED_PROFILE_PROPERTY = 'DogBreed';

/** Breed names are at most a few words; anything longer is not a breed. */
const BREED_NAME_MAX_LENGTH = 100;

/**
 * The dataLayer payload for one pick, or null for a blank one. `breed_name` is its only
 * parameter, so nothing else an announcement carries can reach the dataLayer.
 *
 * @param {unknown} breed the breed's display name, as the modal stored it
 * @returns {{ event: 'pet_breed_selected', breed_name: string } | null}
 */
export function breedPayload(breed) {
  const name =
    typeof breed === 'string'
      ? breed.replace(/\s+/g, ' ').trim().slice(0, BREED_NAME_MAX_LENGTH)
      : '';
  return name ? { event: 'pet_breed_selected', breed_name: name } : null;
}

/**
 * True only when the Customer Privacy API is loaded and says marketing is allowed right now.
 * Strict `=== true`, and an API that throws counts as no.
 *
 * @param {Window} win
 * @returns {boolean}
 */
function marketingAllowed(win) {
  try {
    return win.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
  } catch {
    return false;
  }
}

/**
 * Save the breed on the visitor's Klaviyo profile as `DogBreed`, with marketing consent only.
 *
 * @param {Window} win
 * @param {unknown} breed
 * @returns {boolean} true when Klaviyo was called
 */
export function identifyBreed(win, breed) {
  const payload = breedPayload(breed);
  if (!payload || !win || !marketingAllowed(win)) return false;

  const klaviyo = win.klaviyo;
  if (!klaviyo) return false;

  const properties = { [BREED_PROFILE_PROPERTY]: payload.breed_name };
  try {
    if (typeof klaviyo.identify === 'function') {
      const pending = klaviyo.identify(properties);
      // A blocked klaviyo.js must not surface as an unhandled rejection on the store's pages.
      if (pending && typeof pending.catch === 'function') pending.catch(() => {});
      return true;
    }
    if (typeof klaviyo.push === 'function') {
      klaviyo.push(['identify', properties]);
      return true;
    }
  } catch {
    // A Klaviyo error must never break the breed modal that raised the event.
  }
  return false;
}

/**
 * Listen for the breed modal's announcement. Called once, from this module's start-up. The event
 * name is written out rather than read from a constant: this runs while the module is still
 * being evaluated, where a `const` declared further down would not exist yet.
 *
 * @param {Window} win
 * @returns {boolean} true when the listener was registered
 */
export function listenForBreed(win) {
  const doc = win && win.document;
  if (!doc || typeof doc.addEventListener !== 'function') return false;

  doc.addEventListener('wf:breed-selected', (event) => {
    const payload = breedPayload(event?.detail?.breed);
    if (!payload) return;
    pushEvent(win, payload);
    identifyBreed(win, payload.breed_name);
  });
  return true;
}

/* The auto-start. `typeof document` guards the Vitest run, which has no DOM at all. */
if (typeof document !== 'undefined' && typeof window !== 'undefined') start(window);
